import { defineStore } from 'pinia';
import { type RecruitmentInvite } from '../models/invite';
import { type Application, type DeclinedApplication } from '../models/application';
import { type GameRole } from '../models/game';
import { Firestore, collection, getDocs, query, where, orderBy, deleteDoc, doc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

export interface RecruitmentStoreState {
	openInvites: RecruitmentInvite[];
	myApplications: Application[];
	declinedApplications: DeclinedApplication[];
	gameApplications: Application[]; // GM view for a specific game
	loading: boolean;
}

export const useRecruitmentStore = defineStore('RecruitmentStore', {
	state: (): RecruitmentStoreState => ({
		openInvites: [],
		myApplications: [],
		declinedApplications: [],
		gameApplications: [],
		loading: false,
	}),

	actions: {
		async fetchOpenInvites() {
			this.loading = true;
			const { $firestore } = useNuxtApp();
			const q = query(
				collection($firestore as Firestore, 'invites'),
				where('type', '==', 'game-recruitment'),
				where('status', '==', 'open'),
				orderBy('createdAt', 'desc'),
			);
			const snap = await getDocs(q);
			this.openInvites = snap.docs.map(d => ({
				id: d.id,
				...d.data(),
			} as RecruitmentInvite));
			this.loading = false;
		},

		async fetchMyApplications() {
			const functions = getFunctions();
			const fetch = httpsCallable<object, { applications: Application[] }>(
				functions, 'fetchMyApplications');
			const result = await fetch({});
			this.myApplications = result.data.applications;
		},

		async fetchDeclinedApplications() {
			const userStore = useUserStore();
			if (!userStore.isLoggedIn) return;

			const { $firestore } = useNuxtApp();
			const snap = await getDocs(
				collection($firestore as Firestore,
					`users/${userStore.id}/declined-applications`));
			this.declinedApplications = snap.docs.map(d => ({
				id: d.id,
				...d.data(),
			} as DeclinedApplication));
		},

		async clearDeclined(id: string) {
			const userStore = useUserStore();
			if (!userStore.isLoggedIn) return;

			const { $firestore } = useNuxtApp();
			await deleteDoc(doc($firestore as Firestore,
				`users/${userStore.id}/declined-applications`, id));
			this.declinedApplications = this.declinedApplications
				.filter(d => d.id !== id);
		},

		async createRecruitment(gameId: string, playerSlots: number, gmSlots: number) {
			const functions = getFunctions();
			const create = httpsCallable<object, { success: boolean }>(
				functions, 'createGameRecruitment');
			await create({ gameId, playerSlots, gmSlots });
			await this.fetchOpenInvites();
		},

		async applyToGame(inviteId: string, characterId: string, message: string, role: GameRole) {
			const functions = getFunctions();
			const apply = httpsCallable<object, { success: boolean }>(
				functions, 'applyToGame');
			await apply({ inviteId, characterId, message, role });
			await this.fetchMyApplications();
		},

		async fetchGameApplications(gameId: string) {
			const functions = getFunctions();
			const fetch = httpsCallable<{ gameId: string }, { applications: Application[] }>(
				functions, 'fetchGameApplications');
			const result = await fetch({ gameId });
			this.gameApplications = result.data.applications;
		},

		async acceptApplication(applicationId: string, gameId: string) {
			const functions = getFunctions();
			const accept = httpsCallable<object, { success: boolean }>(
				functions, 'acceptApplication');
			await accept({ applicationId, gameId });
			this.gameApplications = this.gameApplications
				.filter(a => a.id !== applicationId);
		},

		async declineApplication(applicationId: string, gameId: string) {
			const functions = getFunctions();
			const decline = httpsCallable<object, { success: boolean }>(
				functions, 'declineApplication');
			await decline({ applicationId, gameId });
			this.gameApplications = this.gameApplications
				.filter(a => a.id !== applicationId);
		},

		async withdrawApplication(applicationId: string, gameId: string) {
			const functions = getFunctions();
			const withdraw = httpsCallable<object, { success: boolean }>(
				functions, 'withdrawApplication');
			await withdraw({ applicationId, gameId });
			this.myApplications = this.myApplications
				.filter(a => a.id !== applicationId);
		},

		async closeRecruitment(inviteId: string) {
			const functions = getFunctions();
			const close = httpsCallable<object, { success: boolean }>(
				functions, 'closeRecruitment');
			await close({ inviteId });
			this.openInvites = this.openInvites
				.filter(i => i.id !== inviteId);
		},
	},
});
